/**
 * Recarrega a página quando um chunk carregado sob demanda (`import()`) falha.
 *
 * As telas são lazy-loaded em `App.tsx`, então navegar dispara um `import()` de
 * um `/assets/<nome>-<hash>.js`. Esse fetch falha em dois cenários, os dois
 * observados em produção pelo CloudWatch RUM:
 *
 * 1. **Rede instável** — o chunk existe e serve normalmente, mas a conexão caiu
 *    no meio do download. Foi o caso do `TypeError: Failed to fetch dynamically
 *    imported module: /assets/Landing-<hash>.js` em 31/08/2026.
 * 2. **Deploy novo com `index.html` velho em cache** — o hash no nome muda a
 *    cada build, então a aba antiga pede um arquivo que não existe mais. Aqui o
 *    sintoma é confuso: o fallback de SPA do CloudFront (`403/404 → 200
 *    /index.html`, em `infra/lib/frontend-stack.ts`) é da distribuição inteira e
 *    não dá para limitar por path, então o browser recebe HTML no lugar de JS e
 *    estoura `'text/html' is not a valid JavaScript MIME type` (28/08/2026).
 *
 * Recarregar resolve os dois: busca o `index.html` atual e, com ele, os hashes
 * atuais. O evento `vite:preloadError` é o gancho oficial do Vite para isso.
 *
 * Sem `preventDefault()` de propósito: o erro continua subindo para o handler
 * global e sendo reportado ao RUM. Perder essa telemetria esconderia justamente
 * o que precisamos medir — com que frequência isso acontece.
 */

/** Só uma tentativa por janela: se recarregar não resolveu, o problema é outro. */
const CHAVE = 'pwa:ultimo-reload-preload';
const JANELA_MS = 60_000;

/** `sessionStorage` lança em modo privado/cookies bloqueados — nunca deixe quebrar o boot. */
function lerUltimaTentativa(): number | null {
  try {
    const bruto = sessionStorage.getItem(CHAVE);
    if (!bruto) return null;
    const ts = Number(bruto);
    return Number.isFinite(ts) ? ts : null;
  } catch {
    return null;
  }
}

function registrarTentativa(ts: number): void {
  try {
    sessionStorage.setItem(CHAVE, String(ts));
  } catch {
    // Sem storage não há como evitar loop; o guard abaixo assume o pior e
    // recarrega uma vez só por carregamento de página.
  }
}

let recarregouNestaPagina = false;

/**
 * Decide se vale recarregar. Exportado para teste — o efeito colateral
 * (`location.reload()`) fica no listener.
 */
export function devePorRecarregar(agora: number = Date.now()): boolean {
  if (recarregouNestaPagina) return false;
  const ultima = lerUltimaTentativa();
  if (ultima !== null && agora - ultima < JANELA_MS) return false;
  return true;
}

/** Registra o listener. Chamado uma vez no boot (`main.tsx`). */
export function initPreloadErrorHandler(): void {
  window.addEventListener('vite:preloadError', () => {
    const agora = Date.now();
    if (!devePorRecarregar(agora)) return;
    registrarTentativa(agora);
    recarregouNestaPagina = true;
    window.location.reload();
  });
}

/** Reseta o guard de módulo. Apenas para teste. */
export function _resetParaTeste(): void {
  recarregouNestaPagina = false;
}
